import * as Sentry from '@sentry/browser';

import { matchPath } from 'react-router-dom';
import { push, replace } from 'redux-first-history';

import * as HairProfileService from 'Services/HairProfileService';
import { isInvalidZipcode } from 'Services/HTTPError';
import * as UserService from 'Services/UserService';

import { getIsAuthenticated } from 'dux/auth/selectors';
import {
  isPrescriptionPageAbTest,
  isPrescriptionScoringVariantTest,
} from 'dux/featureFlags/selectors';
import { fetchPrescription } from 'dux/prescription/actions';
import * as routerSelectors from 'dux/router/selectors';
import { getStylistPubkey } from 'dux/stylist/selectors';
import { trackConsultationFinish, trackFragranceUpdated } from 'dux/tracking/actions';
import { getHaveCompletedHairProfile, getUserFragrance } from 'dux/user/selectors';

import * as fragrances from 'constants/fragrances';
import { streamlineFragranceLabel } from 'utils/fragrances';
import { fetchSomething } from 'utils/reduxUtils';

import { FINAL_ROUTE, SUPPLEMENT_FINAL_ROUTE } from 'Apps/Consultation/constants';
import consultationSigninSignin from 'Apps/Consultation/questions/consultationSigninSignin';

import {
  BUFFER_ANSWER,
  CLEAR_ANSWER_BUFFER,
  COMPLETE_HAIR_PROFILE,
  CONSULTATION_AGGRESSORS_FAILURE,
  CONSULTATION_AGGRESSORS_REQUEST,
  CONSULTATION_AGGRESSORS_SUCCESS,
  CONSULTATION_FRAGRANCES_FAILURE,
  CONSULTATION_FRAGRANCES_REQUEST,
  CONSULTATION_FRAGRANCES_SUCCESS,
  CONSULTATION_INGREDIENTS_FAILURE,
  CONSULTATION_INGREDIENTS_REQUEST,
  CONSULTATION_INGREDIENTS_SUCCESS,
  CONSULTATION_SCORING_FAILURE,
  CONSULTATION_SCORING_REQUEST,
  CONSULTATION_SCORING_SUCCESS,
  FEEDBACK_FRAGRANCES_FAILURE,
  FEEDBACK_FRAGRANCES_REQUEST,
  FEEDBACK_FRAGRANCES_SUCCESS,
  FINISH_CONSULTATION,
  GET_HAIR_PROFILE_ERROR,
  GET_HAIR_PROFILE_REQUEST,
  GET_HAIR_PROFILE_SUCCESS,
  INITIALIZE_CONSULTATION_ERROR,
  INITIALIZE_CONSULTATION_REQUEST,
  INITIALIZE_CONSULTATION_SUCCESS,
  INVALIDATE_CONSULTATION,
  IS_EMAIL_CAPTURE_EXIT_CONSULTATION_MODAL_OPEN,
  SAVE_ANSWER_ERROR,
  SAVE_ANSWER_REQUEST,
  SAVE_ANSWER_SUCCESS,
} from './actionTypes';
import {
  getAggressorsError,
  getBufferedAnswersData,
  getConsultationAnswers,
  getConsultationQuestionForIndex,
  getCurrentlyLoadedQuestionSet,
  getIsConsultationLoading,
  hasBufferedAnswers,
} from './selectors';

const DEFAULT_LAST_SHAMPOO = 'yesterday_evening';

export const invalidateConsultation = () => ({ type: INVALIDATE_CONSULTATION });

export const initializeConsultationSuccess = answers => ({
  type: INITIALIZE_CONSULTATION_SUCCESS,
  answers,
});

export const clearAnswerBuffer = () => ({ type: CLEAR_ANSWER_BUFFER });

export const initializeConsultation = () => async (dispatch, getState) => {
  const stylistSlug = window._localStorage.getItem('stylist');

  const state = getState();
  const isLoading = getIsConsultationLoading(state);
  const stylistPubkey = getStylistPubkey(state);
  const userHasBufferedAnswers = hasBufferedAnswers(state);

  if (isLoading) {
    return;
  }

  dispatch({
    type: INITIALIZE_CONSULTATION_REQUEST,
  });

  try {
    if (stylistSlug && stylistPubkey) {
      await UserService.patch({ stylist: stylistPubkey });
      window._localStorage.removeItem('stylist');
    }

    const initialConsultation = {
      diag_last_shampoo: DEFAULT_LAST_SHAMPOO,
      privacy_policies: true,
      terms_conditions: true,
      ...(userHasBufferedAnswers ? getBufferedAnswersData(state) : {}),
    };

    const hairProfile = await HairProfileService.patch(initialConsultation);
    if (userHasBufferedAnswers) {
      dispatch({ type: CLEAR_ANSWER_BUFFER });
    }

    dispatch(initializeConsultationSuccess(hairProfile));
  } catch (error) {
    dispatch({
      type: INITIALIZE_CONSULTATION_ERROR,
      error,
    });
  }
};

const goToRoute = route => (dispatch, getState) => {
  // TODO test this standalon logic
  const state = getState();
  const locationSearch = routerSelectors.getSearch(state);
  const params = new URLSearchParams(locationSearch);
  const standalone = params.has('standalone');
  const currentRoute = routerSelectors.getPathname(state);
  const shouldReplaceRoute = currentRoute === consultationSigninSignin.route;

  if (route === FINAL_ROUTE || route === SUPPLEMENT_FINAL_ROUTE) {
    dispatch(trackConsultationFinish());
  }
  return dispatch(
    shouldReplaceRoute // in case of sign-in, we want to remove sign-ing "question" from history stack
      ? replace(standalone ? '/consultation/results/stored' : route)
      : push(standalone ? '/consultation/results/stored' : route)
  );
};

const saveAnswer = (answers, trackingPayload) => async dispatch => {
  dispatch({ type: SAVE_ANSWER_REQUEST, trackingPayload });

  try {
    await HairProfileService.patch(answers);

    return dispatch({
      type: SAVE_ANSWER_SUCCESS,
      answers,
    });
  } catch (error) {
    dispatch({
      type: SAVE_ANSWER_ERROR,
      error,
    });
    throw error;
  }
};

const bufferAnswer = (answers, trackingPayload) => dispatch =>
  dispatch({ type: BUFFER_ANSWER, trackingPayload, answers });

const save = (selected, questionIndex) => (dispatch, getState) => {
  const state = getState();
  const question = getConsultationQuestionForIndex(state, { questionIndex });
  const answers = getConsultationAnswers(state);
  const trackingPayload = {
    answer: selected,
    category: question.category,
    index: questionIndex,
    name: question.name,
  };

  return dispatch(
    saveAnswer(question.getAnswers({ selected, answers, question }), trackingPayload)
  );
};

const buffer = (selected, questionIndex) => (dispatch, getState) => {
  const state = getState();
  const question = getConsultationQuestionForIndex(state, { questionIndex });
  const answers = getConsultationAnswers(state);
  const trackingPayload = {
    answer: selected,
    category: question.category,
    index: questionIndex,
    name: question.name,
  };
  return dispatch(
    bufferAnswer(question.getAnswers({ selected, answers, question }), trackingPayload)
  );
};

export const onNext = (selected, currentQuestionIndex) => async (dispatch, getState) => {
  const state = getState();
  const isAuthenticated = getIsAuthenticated(state);
  const currentQuestion = getConsultationQuestionForIndex(state, {
    questionIndex: currentQuestionIndex,
  });

  try {
    // This question needs to be saved logic
    if (!currentQuestion.skipSave) {
      // This question answers will be buffered
      if (currentQuestion.public && !isAuthenticated) {
        dispatch(buffer(selected, currentQuestionIndex));
      } else {
        // This question answers will be saved
        await dispatch(save(selected, currentQuestionIndex));
      }
    }
    const updatedState = getState();
    const answers = getConsultationAnswers(updatedState);
    const questions = getCurrentlyLoadedQuestionSet(updatedState);
    const nextQuestion = questions.find(
      (question, questionIndex) =>
        questionIndex > currentQuestionIndex &&
        question.shouldBeIncluded({
          isAuthenticated,
          answers,
        })
    );

    const pathname = routerSelectors.getPathname(state);
    const match = matchPath('/:consultationType/*', pathname);
    const nextQuestionRoute =
      nextQuestion?.route ??
      (match?.params?.consultationType === 'supplements-consultation'
        ? SUPPLEMENT_FINAL_ROUTE
        : FINAL_ROUTE);

    return dispatch(goToRoute(nextQuestionRoute));
  } catch (error) {
    Sentry.withScope(scope => {
      scope.setLevel('error');
      Sentry.captureMessage(`[Consultation Action] onNext : ${error.toString()}`);
    });
    return null;
  }
};

export const fetchAggressors = fetchSomething(
  [
    CONSULTATION_AGGRESSORS_REQUEST,
    CONSULTATION_AGGRESSORS_SUCCESS,
    CONSULTATION_AGGRESSORS_FAILURE,
  ],
  () => HairProfileService.fetchAggressors()
);

export const setZipcode = ({ answers, recover }) => async (dispatch, getState) => {
  await dispatch(saveAnswer(answers));

  await dispatch(fetchAggressors());

  const aggressorsError = getAggressorsError(getState());

  if (aggressorsError && isInvalidZipcode(aggressorsError)) {
    // saves again the previous answer to erase invalid zipcode
    await dispatch(saveAnswer(recover));
  }

  if (aggressorsError) {
    // throws to avoid skipping question
    throw aggressorsError;
  }
};

export const fetchFragrances = fetchSomething(
  [
    CONSULTATION_FRAGRANCES_REQUEST,
    CONSULTATION_FRAGRANCES_SUCCESS,
    CONSULTATION_FRAGRANCES_FAILURE,
  ],
  () => HairProfileService.fetchFragrances()
);

export const fetchFragrancesFeedback = fetchSomething(
  [FEEDBACK_FRAGRANCES_REQUEST, FEEDBACK_FRAGRANCES_SUCCESS, FEEDBACK_FRAGRANCES_FAILURE],
  () => HairProfileService.fetchFragrancesFeedback()
);

export const fetchScoring = fetchSomething(
  [CONSULTATION_SCORING_REQUEST, CONSULTATION_SCORING_SUCCESS, CONSULTATION_SCORING_FAILURE],
  () => HairProfileService.fetchScoring()
);

export const fetchIngredients = fetchSomething(
  [
    CONSULTATION_INGREDIENTS_REQUEST,
    CONSULTATION_INGREDIENTS_SUCCESS,
    CONSULTATION_INGREDIENTS_FAILURE,
  ],
  () => HairProfileService.fetchIngredients()
);

export const finishConsultation = () => async (dispatch, getState) => {
  dispatch({ type: FINISH_CONSULTATION });
  const isPrescriptionPageVariant = isPrescriptionPageAbTest(getState());
  const isPrescriptionScoringVariant = isPrescriptionScoringVariantTest(getState());

  try {
    const isHairProfileCompleted = getHaveCompletedHairProfile(getState());

    // toggle hair profile as completed if not yet done
    if (!isHairProfileCompleted) {
      await HairProfileService.patch({ status: 'completed' });
      dispatch({ type: COMPLETE_HAIR_PROFILE });
    }
    if (!isPrescriptionPageVariant) {
      if (!isPrescriptionScoringVariant) {
        await Promise.all([dispatch(fetchScoring()), dispatch(fetchIngredients())]);
      } else {
        await Promise.all([dispatch(fetchPrescription()), dispatch(fetchIngredients())]);
      }
    } else {
      await dispatch(fetchPrescription());
    }
  } catch (error) {
    Sentry.withScope(scope => {
      scope.setLevel('error');
      Sentry.captureMessage(
        `[${
          !isPrescriptionPageVariant ? 'Consultation' : 'Prescription'
        }] finishConsultation: ${error.toString()}`
      );
    });
  }
};

export const fetchHairProfile = () => async dispatch => {
  dispatch({ type: GET_HAIR_PROFILE_REQUEST });
  try {
    const hairProfile = await HairProfileService.get();
    dispatch({ type: GET_HAIR_PROFILE_SUCCESS, answers: hairProfile });
  } catch (error) {
    dispatch({ type: GET_HAIR_PROFILE_ERROR, error });
  }
};

/**
 * Update fragrance (Patch)
 * This method can be used one-shot
 * meaning outside the consultation and the R&R feedback
 */

export const updateFragrance = ({ fragrance, origin }) => async (dispatch, getState) => {
  try {
    const payload = {
      pref_fragrance: fragrance.name !== fragrances.NONE ? fragrance.name : null,
      pref_fragrance_free: fragrance.name === fragrances.NONE,
    };

    const previousFragrance = streamlineFragranceLabel(getUserFragrance(getState()));

    await HairProfileService.patch(payload);

    dispatch({
      type: SAVE_ANSWER_SUCCESS,
      answers: payload,
    });

    const trackingPayload = {
      fragrance_updated_page_origin: origin,
      from_fragrance_selection: previousFragrance,
      new_fragrance_selection:
        fragrance.name !== fragrances.NONE ? fragrance.name : fragrance.label,
    };

    dispatch(await trackFragranceUpdated(trackingPayload));
  } catch (error) {
    Sentry.captureMessage(`API action (Subscriptions.updateFragrance) - ${error?.toString()}`);
  }
};

export const setEmailCaptureExitConsultationModalOpened = isOpen => async dispatch =>
  dispatch({
    type: IS_EMAIL_CAPTURE_EXIT_CONSULTATION_MODAL_OPEN,
    isOpen,
  });
